import { driveSourcesKeys, memberExternalAccountKeys, xeroConnectionKeys } from '@/hooks';
import {
  EXTERNAL_CONNECT_BROADCAST_CHANNEL,
  ExternalConnectBroadcastMessage,
  GOOGLE_DRIVE_SOURCE_BROADCAST_CHANNEL,
  GoogleDriveSourceBroadcastMessage,
  MICROSOFT_TENANT_CONSENT_BROADCAST_CHANNEL,
  MicrosoftTenantConsentBroadcastMessage,
  XERO_HUB_CONNECT_BROADCAST_CHANNEL,
  OauthIntegrationKind,
  XeroHubConnectBroadcastMessage,
} from '@vivport/backend/types';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

type ConnectProvider = ExternalConnectBroadcastMessage['provider'];

const CONNECT_PROVIDER_LABELS: Record<ConnectProvider, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

function isConnectProvider(value: unknown): value is ConnectProvider {
  return value === 'google' || value === 'microsoft';
}

function hasStatusAndReason(candidate: { status?: unknown; reason?: unknown }): boolean {
  if (candidate.status !== 'success' && candidate.status !== 'error') {
    return false;
  }
  return candidate.reason === undefined || typeof candidate.reason === 'string';
}

export function isExternalConnectMessage(value: unknown): value is ExternalConnectBroadcastMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<ExternalConnectBroadcastMessage>;
  if (!isConnectProvider(candidate.provider)) {
    return false;
  }
  if (candidate.type !== `vivport:${candidate.provider}-member-external-connect`) {
    return false;
  }
  return hasStatusAndReason(candidate);
}

export function isXeroHubConnectMessage(value: unknown): value is XeroHubConnectBroadcastMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<XeroHubConnectBroadcastMessage>;
  return candidate.type === 'vivport:xero-hub-connect' && hasStatusAndReason(candidate);
}

export function isMicrosoftTenantConsentMessage(value: unknown): value is MicrosoftTenantConsentBroadcastMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<MicrosoftTenantConsentBroadcastMessage>;
  return candidate.type === 'vivport:microsoft-tenant-consent' && hasStatusAndReason(candidate);
}

export function isGoogleDriveSourceMessage(value: unknown): value is GoogleDriveSourceBroadcastMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<GoogleDriveSourceBroadcastMessage>;
  return candidate.type === 'vivport:google-drive-source' && hasStatusAndReason(candidate);
}

function getConnectErrorMessage(reason: string | undefined): string {
  switch (reason) {
    case 'invalid_state':
      return 'Connection failed: the session was invalid or expired. Please try again.';
    case 'oauth_exchange_failed':
      return 'Connection failed: could not complete the token exchange with the provider.';
    case 'oauth_callback_invalid':
    case 'oauth_callback_failed':
      return 'Connection failed: the callback from the provider was invalid.';
    case 'oauth_provider_error':
    case 'access_denied':
      return 'Connection failed: access was not granted.';
    case undefined:
      return 'Connection failed.';
    default:
      return `Connection failed: ${reason.replace(/_/g, ' ')}.`;
  }
}

export function readableReason(reason: string | undefined): string {
  return reason ? reason.replace(/_/g, ' ') : 'unknown error';
}

export function invalidateAfterAccountConnect(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: memberExternalAccountKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: ['processes'] });
  void queryClient.invalidateQueries({ queryKey: ['process-agent-options'] });
  void queryClient.invalidateQueries({ queryKey: ['process-skill-options'] });
}

export function invalidateAfterXeroConnect(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: xeroConnectionKeys.all });
}

export function invalidateAfterDriveSourceConnect(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: driveSourcesKeys.all });
}

export type ConnectBroadcastResult = { status: 'success' | 'error'; reason?: string };

const BROADCAST_BY_KIND: Record<
  OauthIntegrationKind,
  { channel: string; accepts: (value: unknown) => value is ConnectBroadcastResult; invalidate: (queryClient: QueryClient) => void }
> = {
  'google-account': {
    channel: EXTERNAL_CONNECT_BROADCAST_CHANNEL,
    accepts: (value): value is ConnectBroadcastResult => isExternalConnectMessage(value) && value.provider === 'google',
    invalidate: invalidateAfterAccountConnect,
  },
  'microsoft-account': {
    channel: EXTERNAL_CONNECT_BROADCAST_CHANNEL,
    accepts: (value): value is ConnectBroadcastResult => isExternalConnectMessage(value) && value.provider === 'microsoft',
    invalidate: invalidateAfterAccountConnect,
  },
  'google-drive': {
    channel: GOOGLE_DRIVE_SOURCE_BROADCAST_CHANNEL,
    accepts: isGoogleDriveSourceMessage,
    invalidate: invalidateAfterDriveSourceConnect,
  },
  'microsoft-tenant': {
    channel: MICROSOFT_TENANT_CONSENT_BROADCAST_CHANNEL,
    accepts: isMicrosoftTenantConsentMessage,
    invalidate: invalidateAfterDriveSourceConnect,
  },
  xero: {
    channel: XERO_HUB_CONNECT_BROADCAST_CHANNEL,
    accepts: isXeroHubConnectMessage,
    invalidate: invalidateAfterXeroConnect,
  },
};

export function subscribeToConnectBroadcast(
  kind: OauthIntegrationKind,
  queryClient: QueryClient,
  onResult: (result: ConnectBroadcastResult) => void
): () => void {
  const { channel: channelName, accepts, invalidate } = BROADCAST_BY_KIND[kind];
  const channel = new BroadcastChannel(channelName);
  channel.onmessage = (event: MessageEvent) => {
    if (!accepts(event.data)) {
      return;
    }
    invalidate(queryClient);
    onResult({ status: event.data.status, reason: event.data.reason });
  };
  return () => channel.close();
}

export function useIntegrationConnectBroadcasts(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const accounts = new BroadcastChannel(EXTERNAL_CONNECT_BROADCAST_CHANNEL);
    accounts.onmessage = (event: MessageEvent) => {
      if (!isExternalConnectMessage(event.data)) {
        return;
      }
      invalidateAfterAccountConnect(queryClient);
      if (event.data.status === 'error') {
        toast.error(getConnectErrorMessage(event.data.reason));
        return;
      }
      toast.success(`${CONNECT_PROVIDER_LABELS[event.data.provider]} account connected.`);
    };

    const xero = new BroadcastChannel(XERO_HUB_CONNECT_BROADCAST_CHANNEL);
    xero.onmessage = (event: MessageEvent) => {
      if (!isXeroHubConnectMessage(event.data)) {
        return;
      }
      invalidateAfterXeroConnect(queryClient);
      if (event.data.status === 'error') {
        toast.error(`Xero connection failed: ${readableReason(event.data.reason)}.`);
        return;
      }
      toast.success('Xero authorisation completed.');
    };

    const microsoftTenant = new BroadcastChannel(MICROSOFT_TENANT_CONSENT_BROADCAST_CHANNEL);
    microsoftTenant.onmessage = (event: MessageEvent) => {
      if (!isMicrosoftTenantConsentMessage(event.data)) {
        return;
      }
      invalidateAfterDriveSourceConnect(queryClient);
      if (event.data.status === 'error') {
        toast.error(`Microsoft tenant access failed: ${readableReason(event.data.reason)}.`);
        return;
      }
      toast.success('Microsoft tenant access granted.');
    };

    const googleDrive = new BroadcastChannel(GOOGLE_DRIVE_SOURCE_BROADCAST_CHANNEL);
    googleDrive.onmessage = (event: MessageEvent) => {
      if (!isGoogleDriveSourceMessage(event.data)) {
        return;
      }
      invalidateAfterDriveSourceConnect(queryClient);
      if (event.data.status === 'error') {
        toast.error(`Google Drive connection failed: ${readableReason(event.data.reason)}.`);
        return;
      }
      toast.success('Google Drive connected.');
    };

    return () => {
      accounts.close();
      xero.close();
      microsoftTenant.close();
      googleDrive.close();
    };
  }, [queryClient]);
}
